import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  message,
  Spin,
  Alert,
} from 'antd';
import { SaveOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotAgent, KrybotTemplate, AgentType } from '../../../types/krybot';
import { AgentTypeSelectOptions } from '../AgentTypeSelectOptions';
import { debounce } from '../../../utils/debounce';
import styles from './AgentManagement.module.css';

const { TextArea } = Input;
const { Option } = Select;

interface AgentDetailsTabProps {
  agent: KrybotAgent | null;
  agentData: Partial<KrybotAgent>;
  isCreating: boolean;
  onDataChange: (updates: Partial<KrybotAgent>) => void;
  onSave: () => void;
  onCancel: () => void;
  loading: boolean;
}

export const AgentDetailsTab: React.FC<AgentDetailsTabProps> = ({
  agent,
  agentData,
  isCreating,
  onDataChange,
  onSave,
  onCancel,
  loading,
}) => {
  const [form] = Form.useForm();
  const [availableAgents, setAvailableAgents] = useState<KrybotAgent[]>([]);
  const [templates, setTemplates] = useState<KrybotTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<KrybotTemplate | null>(null);
  const [templateAutoPopulated, setTemplateAutoPopulated] = useState(false);

  const loadAvailableAgents = useCallback(async () => {
    try {
      const agents = await KrybotService.listAgents();
      // Exclude current agent from parent options
      const filtered = agent?.id ? agents.filter(a => a.id !== agent.id) : agents;
      setAvailableAgents(filtered);
    } catch (err) {
      // Silently fail - parent agents are optional
    }
  }, [agent?.id]);

  const loadTemplates = useCallback(async () => {
    try {
      const templateList = await KrybotService.listTemplates();
      const activeTemplates = templateList.filter(t => t.is_active);
      if (agent?.template_id) {
        const currentTemplate = templateList.find(t => t.id === agent.template_id);
        if (currentTemplate && !activeTemplates.find(t => t.id === currentTemplate.id)) {
          activeTemplates.push(currentTemplate);
        }
      }
      setTemplates(activeTemplates);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  }, [agent?.template_id]);

  useEffect(() => {
    loadAvailableAgents();
    loadTemplates();
  }, [loadAvailableAgents, loadTemplates]);

  useEffect(() => {
    if (agentData.template_id && templates.length > 0) {
      const template = templates.find(t => t.id === agentData.template_id);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [agentData.template_id, templates]);

  useEffect(() => {
    form.setFieldsValue({
      name: agentData.name || '',
      display_name: agentData.display_name || '',
      description: agentData.description || '',
      agent_type: agentData.agent_type || '',
      persona_prompt: agentData.persona_prompt || '',
      is_active: agentData.is_active ?? true,
      parent_agent_id: agentData.parent_agent_id || undefined,
      template_id: agentData.template_id || undefined,
    });
  }, [agentData, form]);

  const handleTemplateChange = (templateId: string | undefined) => {
    const updates: Partial<KrybotAgent> = { template_id: templateId };
    
    if (!templateId) {
      setSelectedTemplate(null);
      setTemplateAutoPopulated(false);
      onDataChange(updates);
      return;
    }

    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      
      if (template.metadata) {
        const metadata = template.metadata as { input_schema?: Record<string, unknown>; output_schema?: Record<string, unknown> };
        
        // Only auto-populate if fields are empty
        if (metadata.input_schema && !agentData.input_schema) {
          updates.input_schema = metadata.input_schema;
        }
        if (metadata.output_schema && !agentData.output_schema) {
          updates.output_schema = metadata.output_schema;
        }
        
        if ((metadata.input_schema && !agentData.input_schema) || (metadata.output_schema && !agentData.output_schema)) {
          setTemplateAutoPopulated(true);
          message.info(`Schemas populated from template: ${template.template_key}`);
        }
      }
    }
    
    onDataChange(updates);
  };

  // Ref holds latest handler so debounced callback always invokes current logic
  // (avoids stale closure when onDataChange/form change between renders)
  const handleFormChangeRef = useRef<() => void>();
  handleFormChangeRef.current = () => {
    const values = form.getFieldsValue();
    onDataChange(values);
  };

  const debouncedFormChange = useRef(
    debounce(() => {
      handleFormChangeRef.current?.();
    }, 300)
  ).current;

  return (
    <div className="agent-details-tab">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>Agent Details</h3>
        <Space>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={onSave}
            loading={loading}
          >
            {isCreating ? 'Create Agent' : 'Save Changes'}
          </Button>
          {isCreating && (
            <Button icon={<CloseOutlined />} onClick={onCancel}>
              Cancel
            </Button>
          )}
        </Space>
      </div>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={debouncedFormChange}
        initialValues={{
          name: agentData.name || '',
          display_name: agentData.display_name || '',
          description: agentData.description || '',
          agent_type: agentData.agent_type || '',
          persona_prompt: agentData.persona_prompt || '',
          is_active: agentData.is_active ?? true,
          parent_agent_id: agentData.parent_agent_id || undefined,
          template_id: agentData.template_id || undefined,
        }}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Agent name is required' }]}
        >
          <Input placeholder="Enter agent name" disabled={loading || !isCreating} />
        </Form.Item>

        <Form.Item name="display_name" label="Display Name">
          <Input placeholder="Enter display name" disabled={loading} />
        </Form.Item>

        <Form.Item name="description" label="Description">
          <TextArea
            rows={3}
            placeholder="Enter agent description"
            disabled={loading}
          />
        </Form.Item>

        <Form.Item
          name="agent_type"
          label="Agent Type"
          rules={[{ required: true, message: 'Agent type is required' }]}
        >
          <Select
            placeholder="Select agent type"
            disabled={loading}
            showSearch
            optionFilterProp="children"
          >
            <AgentTypeSelectOptions />
          </Select>
        </Form.Item>

        <Form.Item name="parent_agent_id" label="Parent Agent (Optional)">
          <Select
            placeholder="Select parent agent"
            allowClear
            showSearch
            optionFilterProp="children"
            disabled={loading}
          >
            {availableAgents.map((a) => (
              <Option key={a.id} value={a.id}>
                {a.display_name || a.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="template_id" label="Template (Optional)">
          <Select
            placeholder="Select template for structured output"
            allowClear
            showSearch
            optionFilterProp="children"
            onChange={handleTemplateChange}
            disabled={loading}
          >
            {templates.map((t) => (
              <Option key={t.id} value={t.id}>
                {t.template_key} ({t.template_type})
              </Option>
            ))}
          </Select>
        </Form.Item>

        {templateAutoPopulated && (
          <Alert
            message="Schemas Auto-Populated"
            description="Input and/or output schemas have been automatically populated from the selected template. You can modify them in the Input and Output tabs if needed."
            type="info"
            icon={<InfoCircleOutlined />}
            closable
            onClose={() => setTemplateAutoPopulated(false)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form.Item
          name="persona_prompt"
          label="Persona Prompt"
          rules={[{ required: true, message: 'Persona prompt is required' }]}
        >
          <TextArea
            rows={8}
            placeholder="Enter the persona prompt that defines how this agent behaves..."
            disabled={loading}
          />
        </Form.Item>

        <Form.Item name="is_active" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={loading} />
        </Form.Item>
      </Form>
    </div>
  );
};

